
///
/// Copyright © 2016-2020 The Thingsboard Authors
///
/// Licensed under the Apache License, Version 2.0 (the "License");
/// you may not use this file except in compliance with the License.
/// You may obtain a copy of the License at
///
///     http://www.apache.org/licenses/LICENSE-2.0
///
/// Unless required by applicable law or agreed to in writing, software
/// distributed under the License is distributed on an "AS IS" BASIS,
/// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
/// See the License for the specific language governing permissions and
/// limitations under the License.
///

import { EventEmitter, Injectable, NgZone } from '@angular/core';
import {
  AggregationType,
  AlarmDataCmd,
  AlarmDataUnsubscribeCmd,
  AlarmDataUpdate,
  AttributeScope,
  AttributesSubscriptionCmd, EntityDataCmd, EntityDataUnsubscribeCmd, EntityDataUpdate,
  GetHistoryCmd, isAlarmDataUpdateMsg, isEntityDataUpdateMsg, 
  SubscriptionCmd,
  SubscriptionUpdate,
  TelemetryFeature,
  TelemetryPluginCmdsWrapper,
  TimeseriesSubscriptionCmd, WebsocketDataMsg
} from '../models/telemetry.model';
import { select, Store } from '@ngrx/store';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { AppState, selectAuth } from '../state';
import { EntityId, EntityKeyType, EntityType, IAlarm, PageData } from '../models/entity.model';
import { AuthService } from './auth.service';
import { BehaviorSubject, Subject } from 'rxjs';
import moment from 'moment-es6';
import { clone, onAuthenticationChange } from '../util';
import { debounceTime } from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import { PhoneService } from './phone.service';
import { subscriptionRegistry } from './subscription-debug';
import { WEBSOCKET_CONFIG } from '../websocket.config';

const RECONNECT_INTERVAL = WEBSOCKET_CONFIG.reconnectInterval;
const WS_IDLE_TIMEOUT = WEBSOCKET_CONFIG.idleTimeout;
const MAX_PUBLISH_COMMANDS = WEBSOCKET_CONFIG.maxPublishCommands;

export interface IAlarmUpdate {
  data: PageData<IAlarm> | null
  update: IAlarm[]
  dataUpdateType: any
  errorCode: number
  errorMsg: string | null
  totalEntities: number
}

export interface ISubscriptionError {
  code: number
  message: string
  subscriptionId?: number
}

export interface TelemetrySubscriber {
  subscriptionCommands: any[]
  onReconnected()
  onEntityData(update: EntityDataUpdate)
  onData(update: SubscriptionUpdate): boolean
  onAlarmData (update: AlarmDataUpdate): boolean
  // the server sent an error for this subscription, it will not receive any more data
  onError?(error: ISubscriptionError)
}

export interface IWebsocketState {
  isActive: boolean, isOpening: boolean, isOpened: boolean, isReconnect: boolean
  errorMessage: string
}

type Unsubscriber = () => void


// @dynamic
/*
{
  providedIn: 'root'
}
*/
@Injectable()
export class TelemetryWebsocketService { //  implements TelemetryService

  static loaded = false

  isActive = false;
  isOpening = false;
  isOpened = false;
  isReconnect = false;
  errorMessage = ''

  socketCloseTimer: number;
  reconnectTimer: number;

  lastCmdId = 0;
  subscribersCount = 0;
  subscribersMap = new Map<number, TelemetrySubscriber>();

  reconnectSubscribers = new Set<TelemetrySubscriber>();

  cmdsWrapper = new TelemetryPluginCmdsWrapper();
  telemetryUri: string;

  dataStream: WebSocketSubject<TelemetryPluginCmdsWrapper | WebsocketDataMsg>;
  state = new BehaviorSubject<IWebsocketState>({
    isActive: false, isOpened: false, isOpening: false, isReconnect: false, errorMessage: ''
  })
  
  schedule = new Subject()
  online = false

  constructor(
    private store: Store<AppState>,
    private authService: AuthService,
    public ngZone: NgZone,
    private phone: PhoneService
  ) {
    onAuthenticationChange(this.store).subscribe(x => {
      if (x) {
        this.reset(true)
      } else {
        this.closeSocket()
      }
    })
    this.phone.networkReady.subscribe(x => {
      if (x != this.online) {
        this.online = x
      }
    })

    this.store.select((state) => state.host).subscribe(host => {
      this.telemetryUri = host.ws + "/api/ws/plugins/telemetry"
    })
    this._refresh = this.schedule.pipe(debounceTime(1000)).subscribe(x => {
      this.ngZone.run(() => {})
    })
  }
  _refresh
  
  emitState () {
    let state: IWebsocketState = {
      isActive: this.isActive, isOpened: this.isOpened, isOpening: this.isOpening, 
      isReconnect: this.isReconnect, errorMessage: this.errorMessage
    }
    if (!this.state.value || !isEqual(state, this.state.value))
      this.state.next(state)
  }

  public subscribe(subscriber: TelemetrySubscriber) {
    this.ngZone.runOutsideAngular(() => {
      this.isActive = true;
      subscriber.subscriptionCommands.forEach(
        (subscriptionCommand) => {
          const cmdId = this.nextCmdId();
          this.subscribersMap.set(cmdId, subscriber);
          subscriptionCommand.cmdId = cmdId;
          if (subscriptionCommand instanceof SubscriptionCmd) {
            if (subscriptionCommand.getType() === TelemetryFeature.TIMESERIES) {
              this.cmdsWrapper.tsSubCmds.push(subscriptionCommand as TimeseriesSubscriptionCmd);
            } else {
              this.cmdsWrapper.attrSubCmds.push(subscriptionCommand as AttributesSubscriptionCmd);
            }
          } else if (subscriptionCommand instanceof GetHistoryCmd) {
            this.cmdsWrapper.historyCmds.push(subscriptionCommand);
          } else if (subscriptionCommand instanceof EntityDataCmd) {
            this.cmdsWrapper.entityDataCmds.push(subscriptionCommand);
          } else if (subscriptionCommand instanceof AlarmDataCmd) {
            this.cmdsWrapper.alarmDataCmds.push(subscriptionCommand);
          } else {
            throw new Error('unknown subscription command: ' + subscriptionCommand)
          }
        }
      );
      this.subscribersCount++;
      this.publishCommands();
      this.emitState()
    })
  }

  public update(subscriber: TelemetrySubscriber) {
    if (!this.isReconnect) {
      subscriber.subscriptionCommands.forEach(
        (subscriptionCommand) => {
          if (subscriptionCommand.cmdId && subscriptionCommand instanceof EntityDataCmd) {
            this.cmdsWrapper.entityDataCmds.push(subscriptionCommand);
          }
        }
      );
      this.publishCommands();
    }
  }

  public unsubscribe(subscriber: TelemetrySubscriber) {
    if (this.isActive) {
      subscriber.subscriptionCommands.forEach(
        (subscriptionCommand) => {
          if (subscriptionCommand instanceof SubscriptionCmd) {
            subscriptionCommand.unsubscribe = true;
            if (subscriptionCommand.getType() === TelemetryFeature.TIMESERIES) {
              this.cmdsWrapper.tsSubCmds.push(subscriptionCommand as TimeseriesSubscriptionCmd);
            } else {
              this.cmdsWrapper.attrSubCmds.push(subscriptionCommand as AttributesSubscriptionCmd);
            }
          } else if (subscriptionCommand instanceof EntityDataCmd) {
            const entityDataUnsubscribeCmd = new EntityDataUnsubscribeCmd();
            entityDataUnsubscribeCmd.cmdId = subscriptionCommand.cmdId;
            this.cmdsWrapper.entityDataUnsubscribeCmds.push(entityDataUnsubscribeCmd);
          } else if (subscriptionCommand instanceof AlarmDataCmd) {
            const alarmDataUnsubscribeCmd = new AlarmDataUnsubscribeCmd();
            alarmDataUnsubscribeCmd.cmdId = subscriptionCommand.cmdId;
            this.cmdsWrapper.alarmDataUnsubscribeCmds.push(alarmDataUnsubscribeCmd);
          } else if (subscriptionCommand instanceof GetHistoryCmd) {
          } else {
            throw new Error('unknown subscription command: ' + subscriptionCommand)
          }
          const cmdId = subscriptionCommand.cmdId;
          if (cmdId) {
            this.subscribersMap.delete(cmdId);
          }
        }
      );
      this.reconnectSubscribers.delete(subscriber);
      this.subscribersCount--;
      this.publishCommands();
    }
  }

  private nextCmdId(): number {
    this.lastCmdId++;
    return this.lastCmdId;
  }

  private publishCommands() {
    while (this.isOpened && this.cmdsWrapper.hasCommands()) {
      let cmd = this.cmdsWrapper.preparePublishCommands(MAX_PUBLISH_COMMANDS)
      this.dataStream.next(cmd);
      this.checkToClose();
    }
    this.tryOpenSocket();
  }

  private checkToClose() {
    if (this.subscribersCount === 0 && this.isOpened) {
      if (!this.socketCloseTimer) {
        this.socketCloseTimer = window.setTimeout(
          () => this.closeSocket(), WS_IDLE_TIMEOUT);
      }
    }
  }

  private reset(close: boolean) {
    if (this.socketCloseTimer) {
      clearTimeout(this.socketCloseTimer);
      this.socketCloseTimer = null;
    }
    this.lastCmdId = 0;
    this.subscribersMap.clear();
    this.subscribersCount = 0;
    this.cmdsWrapper.clear();
    if (close) {
      this.closeSocket();
    }
    this.emitState()
  }

  private closeSocket() {
    this.isActive = false;
    if (this.isOpened) {
      this.dataStream.unsubscribe();
    }
    this.emitState()
  }

  private tryOpenSocket() {
    if (!this.online) {
      this.reconnectTimer = window.setTimeout(() => this.tryOpenSocket(), RECONNECT_INTERVAL);
      return
    }
    if (this.isActive) {
      
      if (!this.isOpened && !this.isOpening) {
        this.isOpening = true;
        if (AuthService.isJwtTokenValid()) { 
          this.openSocket(AuthService.getJwtToken());
        } else {
          this.authService.refreshJwtToken().subscribe(() => {
              this.openSocket(AuthService.getJwtToken());
            },
            () => {
              this.isOpening = false;
              // NOTE: removed this, interceptor only should auto logout
              //this.authService.logout(true);
              this.emitState()
            }
          );
        }
        this.emitState()
      }
      if (this.socketCloseTimer) {
        clearTimeout(this.socketCloseTimer);
        this.socketCloseTimer = null;
      }
    }
  }

  private openSocket(token: string) {
    
    const uri = `${this.telemetryUri}?token=${token}`;
    this.dataStream = webSocket(
      {
        url: uri,
        openObserver: {
          next: (e: Event) => {
            this.onOpen();
          }
        },
        closeObserver: {
          next: (e: CloseEvent) => {
            this.onClose(e);
          }
        }
      }
    );

    this.dataStream.subscribe((message) => {
        this.ngZone.runOutsideAngular(() => {
          this.onMessage(message as WebsocketDataMsg);
          
        });
    },
    (error) => {
      this.onError(error);
    });
  }

  private onOpen() {
    this.isOpening = false;
    this.isOpened = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.isReconnect) {
      this.isReconnect = false;
      this.reconnectSubscribers.forEach(
        (reconnectSubscriber) => {
          reconnectSubscriber.onReconnected();
          this.subscribe(reconnectSubscriber);
        }
      );
      this.reconnectSubscribers.clear();
    } else {
      this.publishCommands();
    }
    this.emitState()
  }

  private onMessage(message: WebsocketDataMsg) {
    if (message.errorCode) {
      this.showWsError(message.errorCode, message.errorMsg);
      // an error for a single subscription (e.g. 34 "Too many updates!"), the server stops
      // sending data for it, so the subscriber has to decide what to do (retry)
      const subscriptionId: number = message['subscriptionId'] || message['cmdId']
      const subscriber = subscriptionId ? this.subscribersMap.get(subscriptionId) : null
      if (subscriber && subscriber.onError) {
        subscriber.onError({
          code: message.errorCode, message: message.errorMsg, subscriptionId: subscriptionId
        })
      }
    } else {
      let subscriber: TelemetrySubscriber;
      let hasChanges = false
      if (isEntityDataUpdateMsg(message)) {
        subscriber = this.subscribersMap.get(message.cmdId);
        if (subscriber) {
          hasChanges = subscriber.onEntityData(new EntityDataUpdate(message));
        }
      } else if (isAlarmDataUpdateMsg(message)) {
        subscriber = this.subscribersMap.get(message.cmdId);
        if (subscriber) {
          hasChanges = subscriber.onAlarmData(new AlarmDataUpdate(message));
        }
      } else if (message.subscriptionId) {
        subscriber = this.subscribersMap.get(message.subscriptionId);
        if (subscriber) {
          hasChanges = subscriber.onData(new SubscriptionUpdate(message));
        }
      }
      if (hasChanges) {
        this.schedule.next(1)
      } else { 

      }
    }
    this.checkToClose();
    
  }

  private onError(errorEvent) {
    if (errorEvent) {
      this.errorMessage = errorEvent.toString()
    }
    this.isOpening = false;
    this.emitState()
  }

  private onClose(closeEvent: CloseEvent) {
    if (closeEvent && closeEvent.code > 1001 && closeEvent.code !== 1006) {
      this.showWsError(closeEvent.code, closeEvent.reason);
    }
    this.isOpening = false;
    this.isOpened = false;
    if (this.isActive) {
      if (!this.isReconnect) {
        this.reconnectSubscribers.clear();
        this.subscribersMap.forEach(
          (subscriber) => {
            this.reconnectSubscribers.add(subscriber);
          }
        );
        this.reset(false);
        this.isReconnect = true;
      }
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
      }
      this.reconnectTimer = window.setTimeout(() => this.tryOpenSocket(), RECONNECT_INTERVAL);
    }
    this.emitState()
  }

  private showWsError(errorCode: number, errorMsg: string) {
    let message = errorMsg;
    if (!message) {
      message += `WebSocket Error: error code - ${errorCode}.`;
    }
    this.errorMessage = message
    this.emitState()
    //console.error('IMLEMENT WEBSOCKET ERROR', message)
    //this.store.dispatch(new ActionNotificationShow(
    //  {
    //    message, type: 'error'
    //  }));
  }

}



//import { Injectable } from '@angular/core';
//import { AppState } from '../state';

//import { TelemetryWebsocketService } from 'src/thingsboard/core/ws/telemetry-websocket.service'

/*interface EntitySubscriper {
  keys?: string []
  onData(update: SubscriptionUpdate)
}*/

function now () { return new Date().getTime() }
export type TELEMETRY_MODE = 'HISTORY' | 'TIME-WINDOW'
export interface ITelemetrySettings {
  timeWindow?: number
  interval?: number
  agg?: AggregationType
  startTs?: number, endTs?: number
  mode?: TELEMETRY_MODE
}
export interface ITimeRange {
  min: number, max: number
}


@Injectable()
export class TelemetryService {
  
  onSettingUpdate = new EventEmitter()

  // identical subscriptions share one server subscription
  private pool: SubscriptionPool
  settings: ITelemetrySettings = {
    timeWindow: (1000 * 60 * 60 * 24 * 1),
    interval: 1000 * 60 * 5,
    agg: AggregationType.AVG,
    mode: 'TIME-WINDOW', startTs: 0, endTs: 0
  }

  constructor (public socket: TelemetryWebsocketService, private ngZone: NgZone) {
    this.pool = new SubscriptionPool(this.socket)
    this.loadSettings()
  }

  get subscriptions () { return this.pool.list() }

  loadSettings () {
    let data = JSON.parse(localStorage.getItem('_timewindow'))
    if (typeof data == 'object') {
      Object.assign(this.settings, data)
    }
  }

  updateSettings (opt: ITelemetrySettings) {
    this.settings = Object.assign({}, opt)
    localStorage.setItem('_timewindow', JSON.stringify(this.settings))
    this.pool.updateSettings(this.settings)
    this.onSettingUpdate.emit(clone(this.settings))
  }
  latest (entityId: EntityId<any>, opt: ITelemetrySubscriptionOptions, callbacks: ISubscriptionCallbacks) {
    return this.subscribe(
      entityId, Object.assign({scope: 'LATEST_TELEMETRY'}, opt), callbacks, {}
    )
  }
  subscribe (entityId: EntityId<any>, opt: ITelemetrySubscriptionOptions, callbacks: ISubscriptionCallbacks, settings?: ITelemetrySettings): TelemetrySubscription {
    if (!settings) settings = {...this.settings}
    return this.pool.subscribe(entityId, opt, callbacks, settings)
  }

  subscribeEntities () {
    let cmd = new EntityDataCmd()
    cmd.query = {
      pageLink: {
        page: 0, pageSize: 20,
        sortOrder: {
          key: {
            type: EntityKeyType.ENTITY_FIELD,
            key: 'createdTime'
          },
          direction: 'DESC'
        }
      }, //, timeWindow: 86400000},
      entityFilter: {
        type: 'entityList',
        entityType: 'CUSTOMER'
      },
      

      //searchPropagatedAlarms: true, severityList: ['CRITICAL'],
      // textSearch: null, 
      //statusList: [], typeList: [], // types.concat(['TEST']),
      /*sortOrder: {
        key: {
          key: 'createdTime', type: 'ALARM_FIELD'
        }, direction: 'DESC'
      } */
      //entityFields: [],
      /*{
        type: EntityKeyType.ALARM_FIELD, key: 'status'
      } */
      
      //keyFilters: [],
      //latestValues: [] //{type: 'ENTITY_FIELD', key: 'status'}]

    } as any
    let latestCmd = {
      keys: [{type: EntityKeyType.ALARM_FIELD, key: 'status'}]
    }
    let subscriber: TelemetrySubscriber = {
      onData: (v) => {console.error('ENTITY DATA', v); return false},
      onEntityData: (d) => {console.error('ENTITY DATA', d)},
      onReconnected: () => {
        //console.error('RECONNECT')
      },
      onAlarmData: (d) => { return false },
      subscriptionCommands: [cmd]
    }
    this.socket.subscribe(subscriber)
    return subscriber
  }

  /*subscribeEntityRelations (entityId: EntityId<any>) {
    {
      rootStateEntity?: boolean;
      stateEntityParamName?: string;
      defaultStateEntity?: EntityId;
      rootEntity?: EntityId;
      direction?: EntitySearchDirection;
      filters?: Array<EntityTypeFilter>;
      maxLevel?: number;
      fetchLastLevelOnly?: boolean;
    }
  }*/

  subscribeEntity (entityId: EntityId<any>, onData) {
    let cmd = new EntityDataCmd()
    let entityFilter = {
      //rootStateEntity: entityId,
      //stateEntityParamName: null,
      //defaultStateEntity: entityId,
      rootEntity: entityId,
      direction: 'FROM',
      //filters: [],
      //maxLevel: 1,
      //fetchLastLevelOnly: false,
      type: 'relationsQuery'
      /*singleEntity: {
        entityType: entityId.entityType,
        id: entityId.id
      },
      type: 'singleEntity'*/
    } as any
    let _entityFilter = {
      singleEntity: {
        entityType: entityId.entityType,
        id: entityId.id
      },
      type: 'singleEntity'
    }
    cmd.query = {
      pageLink: {
        page: 0, pageSize: 20,
        sortOrder: {
          key: {
            type: EntityKeyType.ENTITY_FIELD,
            key: 'createdTime'
          },
          direction: 'DESC'
        }
      }, //, timeWindow: 86400000},
      entityFilter: entityFilter,
      
      //searchPropagatedAlarms: true, severityList: ['CRITICAL'],
      // textSearch: null, 
      //statusList: [], typeList: [], // types.concat(['TEST']),
      /*sortOrder: {
        key: {
          key: 'createdTime', type: 'ALARM_FIELD'
        }, direction: 'DESC'
      } */
      entityFields: [{type: EntityKeyType.ENTITY_FIELD, key: 'name'}],
      /*{
        type: EntityKeyType.ALARM_FIELD, key: 'status'
      } */
      
      //keyFilters: [],
      latestValues: [] // [{type: EntityKeyType.TIME_SERIES, key: 'temperature'}]

    } as any
    let latestCmd = {
      keys: [{type: EntityKeyType.ALARM_FIELD, key: 'status'}]
    }
    let subscriber: TelemetrySubscriber = {
      onData: (v) => {console.error('ENTITY DATA', v); return false},
      onEntityData: (d) => {console.error('ENTITY DATA', d)},
      onReconnected: () => {
      },
      onAlarmData: (d) => {
        return onData(d.data)
      },
      subscriptionCommands: [cmd]
    }
    this.socket.subscribe(subscriber)
    return subscriber
  }

  subscribeAlarms (entityId: EntityId<any>, types: string[], onData: (v: AlarmDataUpdate) => boolean) {
    //let cmd = new EntityDataCmd()
    let cmd = new AlarmDataCmd() as any
    /*cmd.query = {
      pageLink: {
        page: 0, pageSize: 1024, timeWindow: 86400000,
        searchPropagatedAlarms: true, severityList: ['CRITICAL'],
        // textSearch: null, 
        statusList: [], typeList: [], // types.concat(['TEST']),
        sortOrder: {
          key: {
            key: 'createdTime', type: 'ALARM_FIELD'
          }, direction: 'DESC'
        }
      },
      entityFilter: {
        alarmFields: [
          {key: 'createdTime', type: 'ALARM_FIELD'},
          {type: 'ALARM_FIELD', key: 'status'}
        ],
        singleEntity: {
          entityType: entityId.entityType,
          id: entityId.id
        },
        type: 'singleEntity',
      },
      entityFields: [{
        type: 'ENTITY_FIELD', key: 'status'
      }],
      latestValues: [{
        type: 'ALARM_FIELD', key: 'createdTime'
      }, {
        type: 'ALARM_FIELD', key: 'status'
      }]
      
    }*/
    cmd.query = {
      alarmFields: [
        {
          type: "ALARM_FIELD",
          key: "createdTime"
        },
        {
          type: "ALARM_FIELD",
          key: "originator"
        },
        {
          type: "ALARM_FIELD",
          key: "type"
        },
        {
          type: "ALARM_FIELD",
          key: "severity"
        },
        {
          type: "ALARM_FIELD",
          key: "status"
        }
      ],
      entityFields: [],
      entityFilter: {
        singleEntity: {
          entityType: entityId.entityType,
          id: entityId.id
        },
        type: "singleEntity"
      },
      latestValues: [],
      pageLink: {
        page: 0,
        pageSize: 50,
        searchPropagatedAlarms: false,
        severityList: [],
        statusList: [],
        textSearch: null,
        timeWindow: 86400000 * 24 * 30,
        typeList: types,
        sortOrder: {
          key: {key: "createdTime", type: "ALARM_FIELD"},
          direction: "DESC"
        }
      }
    }
    let subscriber: TelemetrySubscriber = {
      onData: (v) => {console.error('DATA', v); return false},
      onEntityData: (d) => {console.error('DATA', d)},
      onReconnected: () => {},
      onAlarmData: (d) => {
        return onData(d)
      },
      subscriptionCommands: [cmd]
    }
    this.socket.subscribe(subscriber)
    return subscriber
  }

  toDataArray (data: ITelemetryData) {
    let values = data.data
    let result: ITelemetryDataArray = {data: []}
    for (var k in values) {
      result.data.push({
        dataKey: {name: k}, data: [values[k][0]]
      })
    }
    return result
  }
}

export class TelemetryContext {
  private pool: SubscriptionPool

  constructor (
    private telemetry: TelemetryService, private key: string, 
    private settings: ITelemetrySettings) {
    this.pool = new SubscriptionPool(this.socket)
  }
  
  get socket () { return this.telemetry.socket }
  get subscriptions () { return this.pool.list() }

  latest (entityId: EntityId<any>, opt: ITelemetrySubscriptionOptions, callbacks: ISubscriptionCallbacks) {
    return this.subscribe(
      entityId, Object.assign({scope: 'LATEST_TELEMETRY'}, opt), callbacks
    )
  }
  subscribe (entityId: EntityId<any>, opt: ITelemetrySubscriptionOptions, callbacks: ISubscriptionCallbacks): TelemetrySubscription {
    return this.pool.subscribe(entityId, opt, callbacks, this.settings)
  }
}

function sum (values: number[]) {
  return values.reduce((a,b) => a + b)
}

function average (values: (number)[]) {
  
  return sum(values) / values.length
}
function utcTimestamp () {
  let date = new Date()
  let ts = new Date(date.getTime() + date.getTimezoneOffset() * 60000).getTime();
  return Math.round(ts)
}

export class TimeseriesSubsciption {
  private firstData = true
  private data: {[key: string]: [number, string|number][]} = {}
  private realtime: {[key: string]: [number, string|number][]} = {}
  //private lastTs: null | number = null
  
  private info: ITelemetrySubscriptionInfo
  private options: ITelemetrySubscriptionOptions & ITelemetrySettings
  private subscriber: TelemetrySubscriber = null
  private isLoading = false

  // true once the message with the attributes has arrived (right away when none are requested)
  attributesLoaded = false
  private attrCmdId: number = null

  // retrying after the server sent an error for the subscription
  private retryCount = 0
  private retryTimer: number = null
  private lastErrorTs = 0

  private _s
  constructor (
    private websocket: TelemetryWebsocketService,
    private settings: ITelemetrySettings
    //private ngZone: NgZone
  ) {
    
  }
  
  update (settings: ITelemetrySettings) {
    this.settings = settings
    this.subscribe(this.info)
  }


  getTimerange (): ITimeRange {
    if (this.settings.mode == 'HISTORY') {
      return {min: this.settings.startTs, max: this.settings.endTs}
    } else {
      let maxTs = new Date().getTime() // utcTimestamp()
      let minTs = maxTs - this.settings.timeWindow
      return {
        min: minTs, max: maxTs
      }
    }
  }

  isTimeseries () {
    return this.info.options.scope != 'LATEST_TELEMETRY'
  }

  emitLoading (isLoading: boolean) {
    if (isLoading != this.isLoading) {
      this.isLoading = isLoading
      this.info.callbacks.onLoading(isLoading)
      this.websocket.schedule.next(1)
    }
  }

  private prepareData (data: [number, any][]) {
    data.sort((a, b) => a[0] - b[0])
    return data
  }

  isWithinTimeframe (ts: number) {
    let startTs = this.options.startTs
    let endTs = this.options.endTs

    // timewindows are always to current-time and should not check for endTs
    /*if (!endTs && startTs && this.options.timeWindow) {
      endTs = startTs + this.options.timeWindow
    }*/
    
    if (startTs && startTs > ts) {
      return false // console.warn('timestamp is before subscription start-time', item)
    } else if (endTs && endTs < ts) {
      return false // console.warn('timestamp is before subscription end-time', item)
    }
    return true
  }

  addDataItem (item: [number, object]) {
    // NOTE: this is a special case for adding values to subscription that comes from external source
    //       the external item might not be in correct or even follow the subscription start-end
    let ts = item[0]
    if (!this.isWithinTimeframe(ts)) return
    let data = item[1]
    
    for (var k in data) {
      if (!this.options.keys.includes(k)) {
        continue
      }
      if (!Reflect.has(this.data, k)) {
        this.data[k] = []
      }
      this.data[k].push([ts, data[k]])
    }
    this.info.callbacks.onData({data: this.data, attributesLoaded: this.attributesLoaded})
  }

  emitData (data: {[key: string]: [number, any][]}): boolean {
    if (this.info.options.agg == 'NONE') {
      for (var k in data) {
        if (!Reflect.has(this.data, k)) {
          this.data[k] = data[k]
        } else {
          this.data[k].push(...data[k])
        }
        this.data[k].sort((a, b) => a[0] - b[0])
        // NOTE: for some reason it will still receive data outside timeframe
        // not sure if this is also true for agg != NONE
        this.data[k] = this.data[k].filter(x => {
          return this.isWithinTimeframe(x[0])
        })
      }
      return this.info.callbacks.onData({data: this.data, attributesLoaded: this.attributesLoaded})
    }

    let changes = false
    // attribute keys are plain values, whatever the scope of the subscription is
    let attributeKeys: string[] = this.info.options.attributes || []
    // aggregated timeseries: the first message is the history, already aggregated by the
    // server into buckets of "interval" anchored at startTs. Every later message holds raw
    // samples (the server never aggregates live data), which are folded into their bucket
    // the same way the thingsboard dashboard does it
    let history = this.firstData
    for (var k in data) {
      let values = this.prepareData(data[k])
      if (this.isTimeseries() && !attributeKeys.includes(k)) {
        for (let point of values) {
          if (this.addToBucket(k, point[0], point[1], history)) changes = true
        }
        this.data[k] = this.bucketSeries(k)
      } else if (!Reflect.has(this.data, k) || !isEqual(this.data[k], values)) {
        this.data[k] = values
        changes = true
      }
    }
    if (!changes && !this.firstData) {
      //console.error('NO CHANGES!', data)
      return false
    }

    this.firstData = false
    return this.info.callbacks.onData({data: this.data, attributesLoaded: this.attributesLoaded})
  }

  // one bucket per interval and key, see emitData()
  private buckets: {[key: string]: Map<number, IBucket>} = {}
  private lastRaw: {[key: string]: [number, any]} = {}

  private bucketStart (ts: number) {
    let start = this.options.startTs || 0
    let interval = this.options.interval
    return start + Math.floor((ts - start) / interval) * interval
  }

  private addToBucket (key: string, ts: number, rawValue: any, history: boolean) {
    let value = typeof rawValue == 'string' ? parseFloat(rawValue) : rawValue
    if (typeof value != 'number' || isNaN(value)) return false
    if (!history) {
      // the server sends the same sample again now and then
      let last = this.lastRaw[key]
      if (last && last[0] == ts && last[1] == rawValue) return false
      this.lastRaw[key] = [ts, rawValue]
    }
    if (!this.buckets[key]) this.buckets[key] = new Map()
    let buckets = this.buckets[key]
    let start = this.bucketStart(ts)
    let bucket = buckets.get(start)
    if (!bucket || history) {
      // the value from the server stands for the whole bucket so far
      buckets.set(start, {start: start, sum: value, count: 1, min: value, max: value, value: value})
      return true
    }
    bucket.sum += value
    bucket.count += 1
    bucket.min = Math.min(bucket.min, value)
    bucket.max = Math.max(bucket.max, value)
    bucket.value = this.aggregate(bucket)
    return true
  }

  private aggregate (bucket: IBucket) {
    switch (this.options.agg) {
      case AggregationType.MIN: return bucket.min
      case AggregationType.MAX: return bucket.max
      case AggregationType.SUM: return bucket.sum
      case AggregationType.COUNT: return bucket.count
      default: return bucket.sum / bucket.count
    }
  }

  // the buckets of a key as series points (middle of the bucket, like the server), buckets
  // that have left the time window are dropped
  private bucketSeries (key: string): [number, number][] {
    let interval = this.options.interval
    let minTs = this.getTimerange().min
    let buckets = this.buckets[key]
    let result: [number, number][] = []
    if (!buckets) return result
    for (let bucket of Array.from(buckets.values())) {
      if (bucket.start + interval < minTs) {
        buckets.delete(bucket.start)
        continue
      }
      result.push([bucket.start + interval / 2, bucket.value])
    }
    result.sort((a, b) => a[0] - b[0])
    return result
  }

  private resetBuckets () {
    this.buckets = {}
    this.lastRaw = {}
  }

  subscribe (info: ITelemetrySubscriptionInfo) {
    this.cancelRetry()
    let resubscribe = this.subscriber != null
    if (resubscribe) {
      this.unsubscribe(true)
    }
    //this.lastTs = null
    this.firstData = true
    this.info = info
    
    this.emitLoading(true)

    let defaultOptions: ITelemetrySubscriptionOptions = {
      agg: AggregationType.AVG, limit: 500, //limit: 289, 
      interval: 300000, keys: []
    }
    
    let opt = info.options
    let entityId = info.entity
    let options = Object.assign({}, defaultOptions, this.settings, opt)
    //console.log('subscribe', {...defaultOptions}, {...this.settings}, {...opt})
    this.options = options

    let commands = []
    let attrCmd: AttributesSubscriptionCmd = null
    if (opt.attributes && opt.attributes.length > 0) {
      attrCmd = new AttributesSubscriptionCmd()
      attrCmd.keys = opt.attributes.join(',')
      attrCmd.entityId = entityId.id;
      attrCmd.entityType = entityId.entityType as EntityType;
      commands.push(attrCmd)
    }

    if (options.mode == 'HISTORY') {

      let cmd = new GetHistoryCmd()
      cmd.startTs = options.startTs
      cmd.endTs = options.endTs
      cmd.limit = 100
      cmd.agg = options.agg
      cmd.interval = options.interval
      cmd.keys = options.keys.join(',')
      cmd.entityType = entityId.entityType as EntityType;
      cmd.entityId = entityId.id;
      //console.error('COMMAND', cmd.entityId, cmd)
      commands.push(cmd)
    } else if (opt.keys) {
      delete options.endTs
      let cmd = new TimeseriesSubscriptionCmd()
      if (options.scope == 'LATEST_TELEMETRY') {
        cmd.scope = 'LATEST_TELEMETRY' as AttributeScope
      } else {
        cmd.agg = options.agg
        //cmd.limit = options.limit
        cmd.interval = options.interval
        cmd.timeWindow = options.timeWindow
        options.startTs = now() - options.timeWindow
        cmd.startTs = options.startTs
        
      }
      cmd.keys = options.keys.join(',')
      cmd.entityType = entityId.entityType as EntityType;
      cmd.entityId = entityId.id;
      //console.error('COMMAND', cmd.entityId, cmd)
      commands.push(cmd)
      //console.log(cmd)
    }
    
    this.subscriber = {
      onData: (v) => {
        return this.onData(v)
      },
      onEntityData: (d) => {console.warn('IMPLEMENT onEntityData') },
      onReconnected: () => {
        this.data = {}
        this.realtime = {}
        this.resetBuckets()
      },
      onAlarmData: (d) => {console.error('unexpected alarm data', d); return false},
      subscriptionCommands: commands,
      onError: (error) => {
        this.onError(error)
      }
    }
    this.websocket.subscribe(this.subscriber)
    // the attributes and the telemetry arrive as separate messages, callers need to know when
    // the attributes are in (see attributesLoaded)
    this.attrCmdId = attrCmd ? attrCmd.cmdId : null
    this.attributesLoaded = attrCmd == null

    subscriptionRegistry.register(this, {
      label: opt.label || subscriptionRegistry.labelFor(entityId.id) || (entityId.entityType + ':' + entityId.id.slice(0, 8)),
      entityId: entityId.id,
      entityType: entityId.entityType,
      scope: options.mode == 'HISTORY' ? 'history' : (options.scope == 'LATEST_TELEMETRY' ? 'latest' : 'timeseries'),
      keys: [].concat(opt.attributes || []).concat(opt.keys || []),
      source: opt.source || '?',
      since: Date.now()
    }, resubscribe, this.websocket.subscribersMap.size)
  }

  unsubscribe (resubscribing=false) {
    //console.log('unsubscribe', this.subscriber)
    if (!resubscribing) {
      this.cancelRetry()
    }
    if (!this.subscriber) {
      console.warn('already unsubscribed')
      return
    }
    this.websocket.unsubscribe(this.subscriber)
    this._s?.unsubscribe()
    this.data = {}
    this.realtime = {}
    this.resetBuckets()
    this.subscriber = null
    subscriptionRegistry.unregister(this, resubscribing, this.websocket.subscribersMap.size)
    this.emitData(this.data)
  }

  // the data received so far
  currentData () {
    return this.data
  }

  isSubscribed () { return this.subscriber != null}

  private get label () {
    return subscriptionRegistry.get(this)?.label || this.info?.entity?.id
  }

  // the server stopped sending data for this subscription, do a full resubscription after a
  // while, waiting longer for each retry until it gives up (see WEBSOCKET_CONFIG)
  private onError (error: ISubscriptionError) {
    if (!this.subscriber || this.retryTimer != null) return
    if (this.retryCount >= WEBSOCKET_CONFIG.maxRetries) {
      subscriptionRegistry.debug('giving up on ' + this.label + ' after ' + this.retryCount + ' retries: ' + error.message)
      this.isLoading = false
      if (this.info.callbacks.onError) {
        this.info.callbacks.onError(error)
      }
      return
    }
    const delay = WEBSOCKET_CONFIG.retryDelay * Math.pow(2, this.retryCount)
    this.retryCount += 1
    this.lastErrorTs = Date.now()
    let registryInfo = subscriptionRegistry.get(this)
    if (registryInfo) registryInfo.retries = this.retryCount
    subscriptionRegistry.debug('retry ' + this.retryCount + '/' + WEBSOCKET_CONFIG.maxRetries + ' of ' + this.label + ' in ' + (delay / 1000) + 's: ' + error.message)
    this.emitLoading(true)
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null
      if (!this.subscriber) return
      this.subscribe(this.info)
    }, delay)
  }

  private cancelRetry () {
    if (this.retryTimer != null) {
      window.clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
  }

  // start over after the retries were given up
  retry () {
    if (!this.info) return
    this.retryCount = 0
    this.subscribe(this.info)
  }

  onData (data: ITelemetryData): boolean {
    if (this.attrCmdId != null && data.subscriptionId == this.attrCmdId) {
      this.attributesLoaded = true
    }
    // the server sends data right after every resubscription, so the retries only start over
    // once the subscription has been working for longer than the longest retry wait
    if (this.retryCount > 0 && Date.now() - this.lastErrorTs > WEBSOCKET_CONFIG.retryDelay * Math.pow(2, WEBSOCKET_CONFIG.maxRetries)) {
      this.retryCount = 0
    }
    let result = this.emitData(data.data)
    this.emitLoading(false)
    return result
  }
}

/*
  Shared subscriptions: everything subscribing with an identical key (entity, scope, keys,
  attributes and time settings) shares one server subscription. Each owner keeps its own
  callbacks, and the server subscription is only released WEBSOCKET_CONFIG.graceDelay after
  the last owner has unsubscribed, so it can be handed to the next owner instead.
*/

function subscriptionKey (entity: EntityId<any>, opt: ITelemetrySubscriptionOptions, settings: ITelemetrySettings) {
  const defaults: ITelemetrySubscriptionOptions = {
    agg: AggregationType.AVG, limit: 500, interval: 300000, keys: []
  }
  const options: ITelemetrySubscriptionOptions & ITelemetrySettings = Object.assign({}, defaults, settings, opt)
  const keys = [].concat(opt.keys || []).sort().join(',')
  const attributes = [].concat(opt.attributes || []).sort().join(',')
  let key = entity.entityType + ':' + entity.id + '|' + (opt.scope || 'timeseries') + '|' + keys + '|' + attributes
  // the time settings do not matter for latest values
  if (opt.scope != 'LATEST_TELEMETRY') {
    key += '|' + [options.agg, options.interval, options.timeWindow, options.mode, options.startTs, options.endTs, options.limit].join('|')
  }
  return key
}

// what a caller of subscribe()/latest() gets: its own view on a possibly shared subscription
export class TelemetrySubscription {
  closed = false

  constructor (
    private shared: SharedSubscription,
    public callbacks: ISubscriptionCallbacks,
    public label: string,
    public source: string
  ) {}

  unsubscribe () {
    if (this.closed) {
      console.warn('already unsubscribed', this.label)
      return
    }
    this.closed = true
    this.shared.detach(this)
  }

  // start over after the retries were given up
  retry () {
    this.shared.underlying.retry()
  }

  update (settings: ITelemetrySettings) {
    this.shared.underlying.update(settings)
  }

  getTimerange () {
    return this.shared.underlying.getTimerange()
  }

  addDataItem (item: [number, object]) {
    this.shared.underlying.addDataItem(item)
  }

  isSubscribed () {
    return !this.closed && this.shared.underlying.isSubscribed()
  }

  isTimeseries () {
    return this.shared.underlying.isTimeseries()
  }
}

class SharedSubscription {
  underlying: TimeseriesSubsciption
  owners = new Set<TelemetrySubscription>()
  hasData = false
  isLoading = false
  failed = false
  private releaseTimer: number = null

  constructor (
    private pool: SubscriptionPool,
    public key: string,
    socket: TelemetryWebsocketService,
    public settings: ITelemetrySettings,
    public entity: EntityId<any>,
    public options: ITelemetrySubscriptionOptions
  ) {
    this.underlying = new TimeseriesSubsciption(socket, settings)
    this.underlying.subscribe({
      entity: entity, options: options, callbacks: {
        onData: (value) => {
          this.hasData = true
          this.failed = false
          let changes = false
          this.owners.forEach(owner => {
            if (owner.callbacks.onData(value)) changes = true
          })
          return changes
        },
        onLoading: (isLoading) => {
          this.isLoading = isLoading
          this.owners.forEach(owner => owner.callbacks.onLoading(isLoading))
        },
        onError: (error) => {
          this.failed = true
          this.owners.forEach(owner => {
            if (owner.callbacks.onError) owner.callbacks.onError(error)
          })
        }
      }
    })
  }

  attach (owner: TelemetrySubscription) {
    this.cancelRelease()
    this.owners.add(owner)
    this.updateRegistry()
    // same as a subscription of its own: it starts out loading
    owner.callbacks.onLoading(true)
    if (this.failed) {
      // a new owner deserves a fresh attempt
      this.underlying.retry()
    } else if (this.hasData) {
      // hand over what has been received so far, outside of the caller's subscribe() call
      queueMicrotask(() => {
        if (owner.closed) return
        owner.callbacks.onData({data: this.underlying.currentData(), attributesLoaded: this.underlying.attributesLoaded})
        if (!this.isLoading) owner.callbacks.onLoading(false)
      })
    }
  }

  detach (owner: TelemetrySubscription) {
    if (!this.owners.delete(owner)) return
    this.updateRegistry()
    // same as a subscription of its own: it ends with an empty update
    owner.callbacks.onData({data: {}})
    if (this.owners.size == 0) {
      this.releaseTimer = window.setTimeout(() => {
        this.releaseTimer = null
        this.release()
      }, WEBSOCKET_CONFIG.graceDelay)
    }
  }

  private cancelRelease () {
    if (this.releaseTimer != null) {
      window.clearTimeout(this.releaseTimer)
      this.releaseTimer = null
    }
  }

  release () {
    this.cancelRelease()
    this.pool.remove(this)
    this.underlying.unsubscribe()
  }

  private updateRegistry () {
    let info = subscriptionRegistry.get(this.underlying)
    if (info) info.owners = this.owners.size
  }
}

class SubscriptionPool {
  private shared = new Map<string, SharedSubscription>()

  constructor (private socket: TelemetryWebsocketService) {}

  subscribe (entity: EntityId<any>, opt: ITelemetrySubscriptionOptions, callbacks: ISubscriptionCallbacks, settings: ITelemetrySettings): TelemetrySubscription {
    const key = subscriptionKey(entity, opt, settings)
    let shared = this.shared.get(key)
    if (!shared) {
      shared = new SharedSubscription(this, key, this.socket, settings, entity, opt)
      this.shared.set(key, shared)
    }
    const owner = new TelemetrySubscription(shared, callbacks, opt.label, opt.source)
    shared.attach(owner)
    return owner
  }

  remove (shared: SharedSubscription) {
    if (this.shared.get(shared.key) == shared) {
      this.shared.delete(shared.key)
    }
  }

  list () {
    return Array.from(this.shared.values())
  }

  // new time settings: every timeseries subscription re-subscribes once, for all its owners
  updateSettings (settings: ITelemetrySettings) {
    const entries = this.list().filter(s => s.underlying.isTimeseries() && s.underlying.isSubscribed())
    entries.forEach(shared => {
      this.shared.delete(shared.key)
      shared.settings = settings
      shared.underlying.update(settings)
      shared.key = subscriptionKey(shared.entity, shared.options, settings)
      this.shared.set(shared.key, shared)
    })
  }
}

function printKeyValue (ts: [number, string]) {
  //let d = new Date().getTime() - 
  return moment(ts[0]).fromNow() + ' ' + ts[1]
}

export interface ITelemetrySubscriptionInfo {
  entity: EntityId<any>
  options: ITelemetrySubscriptionOptions
  callbacks: ISubscriptionCallbacks
}
interface IBucket {
  start: number, sum: number, count: number, min: number, max: number, value: number
}

export interface ITelemetryData {
  subscriptionId?: number
  data: {[key: string]: [number, any][]}
  // whether the requested attributes have been received (they come as a separate message)
  attributesLoaded?: boolean
}
export interface ITelemetryDataArray {
  data: ITelemetryKeyData[]
}
export interface ITelemetryKeyData {
  dataKey: {name: string}, data: [number, any][]
}

interface ISubscriptionCallbacks {
  onData: (value: ITelemetryData) => boolean
  onLoading: (isLoading: boolean) => void
  // the server sent an error and all retries failed, the subscription is dead until retry()
  onError?: (error: ISubscriptionError) => void
}

interface ITelemetrySubscriptionOptions {
  scope?: 'LATEST_TELEMETRY'
  // only used for debugging: who is subscribing and to what
  label?: string
  source?: string
  startTs?: number
  keys?: string[]
  attributes?: string[]
  agg?: AggregationType
  timeWindow?: number
  limit?: number
  interval?: number
}
